<!-- A time zone menu button, shared by Profile → Settings, the /2026 intro and Key Dates, and the
     Program pages (2026-10-14). The menu's first level is the usual zones (`options`), then
     "Other time zones ›": that switches the menu, in place, to the regions (no Etc or UTC), and a
     region to its zones; "‹ Back" at the top goes up a level (DESIGN.md § Multi-level menus). The
     menu is position: fixed, so a dialog doesn't clip it; scrolling the page under it closes it.
     Accessibility: the WAI-ARIA menu button pattern. Enter, Space or Down opens the menu on the
     zone shown (Up: on the last item); Up/Down/Home/End and a letter move; Right, Enter or Space
     opens a level; Left or Escape goes back a level; Escape on the first level closes the menu;
     Tab or a click outside closes it. Each level names the menu ("Regions", "Australia").
     The button's look is the parent's (:deep(.trigger)); its content is the default slot. -->
<template>
  <span ref="rootRef" class="timeZoneMenu" @focusout="onFocusOut">
    <!-- isInline: a span with a button's role, so the label wraps with the text around it (a
         button can't break across lines; Seán, 2026-10-22: "London dates and times") -->
    <component
      :is="isInline ? 'span' : 'button'"
      :id="buttonId"
      ref="buttonRef"
      :type="isInline ? undefined : 'button'"
      :role="isInline ? 'button' : undefined"
      :tabindex="isInline ? 0 : undefined"
      class="trigger"
      aria-haspopup="menu"
      :aria-expanded="isOpen ? 'true' : 'false'"
      :aria-controls="menuId"
      :aria-label="label"
      @click="toggle"
      @keydown="onButtonKeydown">
      <slot />
    </component>
    <div
      v-show="isOpen"
      :id="menuId"
      ref="menuRef"
      role="menu"
      tabindex="-1"
      class="menu"
      :style="menuStyle"
      :aria-labelledby="view ? undefined : buttonId"
      :aria-label="view ? viewName : undefined"
      @keydown="onMenuKeydown"
      @mouseover="onHover">
      <div v-if="view" role="menuitem" tabindex="-1" class="item back" data-back @click="goBack">
        ‹ Back
      </div>
      <template v-if="!view">
        <div
          v-for="zone in options"
          :key="zone"
          role="menuitemradio"
          tabindex="-1"
          class="item"
          :aria-checked="zone === selected ? 'true' : 'false'"
          :data-zone="zone"
          @click="pick(zone)">
          {{ getOptionLabel(zone) }}
        </div>
        <div role="separator" class="separator" />
        <div
          role="menuitem"
          tabindex="-1"
          class="item next other"
          data-view="regions"
          @click="showView('regions')">
          Other time zones<span class="arrow" aria-hidden="true">›</span>
        </div>
      </template>
      <template v-else-if="view === 'regions'">
        <div
          v-for="region in regions"
          :key="region.name"
          role="menuitem"
          tabindex="-1"
          class="item next"
          :class="{hasSelected: region.name === selectedRegion}"
          :data-view="region.name"
          @click="showView(region.name)">
          {{ region.name }}<span class="arrow" aria-hidden="true">›</span>
        </div>
      </template>
      <template v-else>
        <div
          v-for="zone in regionZones"
          :key="zone"
          role="menuitemradio"
          tabindex="-1"
          class="item"
          :aria-checked="zone === selected ? 'true' : 'false'"
          :data-zone="zone"
          @click="pick(zone)">
          {{ getCityLabel(zone) }}
        </div>
      </template>
    </div>
  </span>
</template>

<script setup>
import {computed, nextTick, ref, useId} from "vue";
import {onClickOutside, useEventListener} from "@vueuse/core";
import {getZoneCity} from "@/composables/useLocalEventDates";
import {
  getCityLabel, getKeyTarget, getLabel, getMenuPosition, getOrigin, getRegionName, getRegions
} from "@/components/timeZoneLevels";

// options: the first level's zones; selected: the zone shown; label: the button's accessible
// name (the visible text first, WCAG 2.5.3); isFullName: "Australia/Sydney", not "Sydney".
// "pick" (zone): a zone was chosen (the menu has closed, focus is back on the button)
const props = defineProps({
  options: {type: Array, required: true},
  selected: {type: String, default: ""},
  label: {type: String, required: true},
  isFullName: {type: Boolean, default: false},
  isInline: {type: Boolean, default: false} // in running text: wraps with it (a span, role button)
});
const emit = defineEmits(["pick"]);

const allZones = typeof Intl.supportedValuesOf === "function" ? Intl.supportedValuesOf("timeZone")
  : [];
const regions = getRegions(allZones);

function getItems(menu) {
  return [...(menu?.querySelectorAll("[role^=menuitem]") || [])];
}

// focus without scrolling the page under the fixed menu (that would close it): only the menu
// scrolls, to show the item
function focusItem(item) {
  if (!item) return;
  item.focus({preventScroll: true});
  const menu = item.closest("[role=menu]");
  const overflow = item.offsetTop + item.offsetHeight - menu.clientHeight;
  if (item.offsetTop < menu.scrollTop) menu.scrollTop = item.offsetTop;
  if (overflow > menu.scrollTop) menu.scrollTop = overflow;
}

// the zone shown, else the first item after Back
function focusChecked(menu) {
  const items = getItems(menu);
  const checked = items.find((item) => item.getAttribute("aria-checked") === "true");
  focusItem(checked || items.find((item) => !item.hasAttribute("data-back")));
}

function stopEscape(event) {
  event.preventDefault();
  event.stopPropagation(); // a dialog around the menu stays open
}

const [buttonId, menuId] = [useId(), useId()];
const [rootRef, buttonRef, menuRef] = [ref(null), ref(null), ref(null)];
const isOpen = ref(false);
const view = ref(""); // the menu's level: "" (the options), "regions", or a region's name
const menuStyle = ref({});
const viewName = computed(() => (view.value === "regions" ? "Regions" : view.value));
const selectedRegion = computed(() => getRegionName(props.selected));
const regionZones = computed(() => {
  return regions.find((region) => region.name === view.value)?.zones || [];
});

function getOptionLabel(zone) {
  return props.isFullName ? getLabel(zone) : getZoneCity(zone);
}

// where: "selected" (the zone shown) or "last"
async function open(where = "selected") {
  const origin = getOrigin(rootRef.value);
  // wrapped over two lines (isInline), the menu opens from where the label ends
  const rects = buttonRef.value.getClientRects();
  const rect = rects[rects.length - 1] || buttonRef.value.getBoundingClientRect();
  menuStyle.value = getMenuPosition(rect, origin);
  [view.value, isOpen.value] = ["", true];
  await nextTick();
  if (where === "last") return focusItem(getItems(menuRef.value).at(-1));
  focusChecked(menuRef.value);
}

function close(isRefocused = false) {
  if (!isOpen.value) return;
  isOpen.value = false;
  if (isRefocused) buttonRef.value?.focus();
}

function toggle() {
  if (isOpen.value) return close(true);
  open();
}

// cameFrom: going back, focus lands on the item that opened the level left
async function showView(nextView, cameFrom = "") {
  view.value = nextView;
  await nextTick();
  menuRef.value.scrollTop = 0;
  if (!cameFrom) return focusChecked(menuRef.value);
  focusItem(menuRef.value.querySelector(`[data-view="${cameFrom}"]`));
}

function goBack() {
  showView(view.value === "regions" ? "" : "regions", view.value);
}

function pick(zone) {
  close(true);
  emit("pick", zone);
}

function onButtonKeydown(event) {
  // a span (isInline) has no button's keys of its own: Enter and Space open the menu
  if (props.isInline && ["Enter", " "].includes(event.key)) {
    event.preventDefault();
    return toggle();
  }
  const where = {ArrowDown: "selected", ArrowUp: "last"}[event.key];
  if (!where) return;
  event.preventDefault();
  open(where);
}

// Tab, Enter/Space and the moves
function handleMenuKey(event) {
  const items = getItems(menuRef.value);
  const index = items.indexOf(document.activeElement);
  const isAction = ["Enter", " "].includes(event.key);
  if (event.key === "Tab") return close();
  if (isAction) event.preventDefault();
  if (isAction) return items[index]?.click();
  const target = getKeyTarget(event.key, items, index);
  if (target < 0) return;
  event.preventDefault();
  focusItem(items[target]);
}

function onMenuKeydown(event) {
  const nextView = document.activeElement?.dataset?.view;
  const isBack = event.key === "Escape" || (event.key === "ArrowLeft" && view.value);
  if (isBack) stopEscape(event);
  if (isBack) return view.value ? goBack() : close(true);
  if (event.key !== "ArrowRight" || !nextView) return handleMenuKey(event);
  event.preventDefault();
  showView(nextView);
}

// the pointer moves focus, as the arrows do: one highlighted item
function onHover(event) {
  const item = event.target.closest("[role^=menuitem]");
  if (item && item !== document.activeElement) focusItem(item);
}

// checked once focus has settled: a level's switch removes the focused item before focus moves
// to the new level (showView)
function onFocusOut() {
  setTimeout(() => {
    if (!rootRef.value?.contains(document.activeElement)) close();
  });
}

// the menu is fixed: scrolling the page under it, or resizing, closes it; not a scroll of the
// <dialog> it's fixed to (the dialog's transform makes the menu scroll with it)
function onScroll(event) {
  if (!isOpen.value || rootRef.value?.contains(event.target)) return;
  if (event.target === rootRef.value?.closest("dialog")) return;
  close();
}

onClickOutside(rootRef, () => close());
useEventListener(window, "scroll", onScroll, {capture: true, passive: true});
useEventListener(window, "resize", () => close());
</script>

<style scoped>
/* the menu's colours: the page's, unless the parent sets --menu-* (Profile: its white cards) */
.menu {
  position: fixed;
  z-index: 1000;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: 4px 0;
  font-size: 14px;
  line-height: 1.4;
  text-align: left;
  white-space: normal;
  color: var(--menu-text, var(--text-color));
  background: var(--menu-background, var(--background-color));
  border: 1px solid var(--menu-border, var(--border-color-subtle));
  border-radius: 8px;
  box-shadow: 0 6px 20px var(--menu-shadow, var(--box-shadow-color));
}
.menu:focus {
  outline: none;
}
.separator {
  margin: 4px 0;
  border-top: 1px solid var(--menu-border, var(--border-color-subtle));
}
.item {
  padding: 5px 12px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: pointer;
}
.item:focus {
  background: var(--menu-highlight, var(--background-color-subtle));
  outline: none;
}
.item:focus-visible {
  outline: 2px solid var(--menu-focus, var(--link-color-subtle));
  outline-offset: -2px;
}
.item[aria-checked="true"],
.hasSelected {
  font-weight: 700;
}
.next {
  display: flex;
  justify-content: space-between;
}
.arrow {
  margin-left: 12px;
  opacity: 0.6;
}
.other,
.back {
  opacity: 0.75;
}
.back {
  border-bottom: 1px solid var(--menu-border, var(--border-color-subtle));
  margin-bottom: 4px;
}
/* the zone already chosen: no hover highlight (the mouse moves the focus), as a click changes
   nothing; the keyboard's ring stays (DESIGN.md § 15) */
.item[aria-checked="true"]:focus:not(:focus-visible) {
  background: none;
}
</style>
